import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Save, RotateCcw, ChevronDown, ChevronUp, Edit3, X, Plus, Trash2, GripVertical, Type, Palette, AlignLeft, AlignCenter, AlignRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;
function getToken() {
  return localStorage.getItem('token') || sessionStorage.getItem('token') || '';
}

const FONT_SIZES = ['10px', '11px', '12px', '13px', '14px', '16px', '18px', '20px'];
const BG_COLORS = [
  { value: '', label: 'Aucun', color: '#ffffff' },
  { value: '#f0f9ff', label: 'Bleu clair', color: '#f0f9ff' },
  { value: '#fefce8', label: 'Jaune clair', color: '#fefce8' },
  { value: '#fef2f2', label: 'Rouge clair', color: '#fef2f2' },
  { value: '#f0fdf4', label: 'Vert clair', color: '#f0fdf4' },
  { value: '#faf5ff', label: 'Violet clair', color: '#faf5ff' },
  { value: '#fff7ed', label: 'Orange clair', color: '#fff7ed' },
  { value: '#f8fafc', label: 'Gris clair', color: '#f8fafc' },
  { value: '#fafafa', label: 'Neutre', color: '#fafafa' },
];

const DYNAMIC_VARS = [
  '{{type_simulateur}}', '{{raison_sociale}}', '{{siret}}', '{{reference_rcds}}',
  '{{date_debut}}', '{{date_fin}}', '{{siege_social}}', '{{statut_juridique}}',
  '{{code_naf}}', '{{pays}}', '{{territoire}}'
];

function BlockEditor({ block, onSave, onReset, onDelete, isSelected, onSelect }) {
  const [editing, setEditing] = useState(false);
  const [editLabel, setEditLabel] = useState(false);
  const [label, setLabel] = useState(block.label || '');
  const [content, setContent] = useState(block.content || '');
  const [fontSize, setFontSize] = useState(block.font_size || '12px');
  const [bgColor, setBgColor] = useState(block.bg_color || '');
  const [textAlign, setTextAlign] = useState(block.text_align || 'left');
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    setContent(block.content || '');
    setLabel(block.label || '');
    setFontSize(block.font_size || '12px');
    setBgColor(block.bg_color || '');
    setTextAlign(block.text_align || 'left');
  }, [block]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(block.key, { content, label, font_size: fontSize, bg_color: bgColor, text_align: textAlign });
      setEditing(false);
      setEditLabel(false);
    } catch { toast.error('Erreur sauvegarde'); }
    setSaving(false);
  };

  const handleCancel = () => {
    setContent(block.content || '');
    setLabel(block.label || '');
    setFontSize(block.font_size || '12px');
    setBgColor(block.bg_color || '');
    setTextAlign(block.text_align || 'left');
    setEditing(false);
    setEditLabel(false);
  };

  const insertVar = (v) => {
    setContent(prev => prev + v);
  };

  const preview = (block.content || '').length > 100 ? (block.content || '').substring(0, 100) + '...' : (block.content || '');

  return (
    <div
      className={`border rounded-lg bg-white overflow-hidden transition-all ${isSelected ? 'border-red-400 ring-2 ring-red-100' : 'border-gray-200'}`}
      style={bgColor ? { backgroundColor: bgColor } : {}}
      data-testid={`block-${block.key}`}
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-50 border-b border-gray-100">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onSelect(block.key)}
          className="w-4 h-4 rounded border-gray-300"
          onClick={(e) => e.stopPropagation()}
          data-testid={`block-select-${block.key}`}
        />
        <GripVertical size={14} className="text-gray-400 cursor-grab" />
        <span className="text-xs font-mono bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded min-w-[24px] text-center">{block.order}</span>

        {editLabel ? (
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="flex-1 text-sm font-medium border border-blue-300 rounded px-2 py-0.5 focus:ring-1 focus:ring-blue-500"
            autoFocus
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
            data-testid={`block-label-input-${block.key}`}
          />
        ) : (
          <span
            className="flex-1 font-medium text-sm text-gray-800 cursor-pointer hover:text-blue-600"
            onDoubleClick={() => { setEditLabel(true); setExpanded(true); setEditing(true); }}
            title="Double-cliquez pour renommer"
          >
            {block.label}
          </span>
        )}

        <div className="flex items-center gap-1">
          {block.is_custom && (
            <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">Custom</span>
          )}
          {block.updated_by && block.updated_by !== 'system_seed' && (
            <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded">Modifie</span>
          )}
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setExpanded(!expanded)}>
            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
        </div>
      </div>

      {/* Preview collapsed */}
      {!expanded && (
        <div className="px-3 py-1.5 text-xs text-gray-400 truncate cursor-pointer" onClick={() => setExpanded(true)}>{preview}</div>
      )}

      {/* Expanded content */}
      {expanded && (
        <div className="p-3 space-y-3">
          {editing ? (
            <>
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                {/* Font Size */}
                <div className="flex items-center gap-1">
                  <Type size={13} className="text-gray-500" />
                  <select
                    value={fontSize}
                    onChange={(e) => setFontSize(e.target.value)}
                    className="text-xs border border-gray-300 rounded px-1 py-0.5 bg-white"
                    data-testid={`block-fontsize-${block.key}`}
                  >
                    {FONT_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="w-px h-5 bg-gray-300" />

                {/* Alignment */}
                <div className="flex items-center gap-0.5">
                  {[
                    { val: 'left', icon: AlignLeft },
                    { val: 'center', icon: AlignCenter },
                    { val: 'right', icon: AlignRight },
                  ].map(({ val, icon: Icon }) => (
                    <button
                      key={val}
                      onClick={() => setTextAlign(val)}
                      className={`p-1 rounded ${textAlign === val ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    >
                      <Icon size={14} />
                    </button>
                  ))}
                </div>

                <div className="w-px h-5 bg-gray-300" />

                {/* Background Color */}
                <div className="flex items-center gap-1">
                  <Palette size={13} className="text-gray-500" />
                  <div className="flex gap-0.5">
                    {BG_COLORS.map(c => (
                      <button
                        key={c.value}
                        onClick={() => setBgColor(c.value)}
                        title={c.label}
                        className={`w-5 h-5 rounded border-2 transition-all ${bgColor === c.value ? 'border-blue-500 scale-110' : 'border-gray-300'}`}
                        style={{ backgroundColor: c.color }}
                      />
                    ))}
                  </div>
                </div>

                <div className="w-px h-5 bg-gray-300" />

                {/* Variables */}
                <div className="flex items-center gap-1 flex-wrap">
                  <span className="text-[10px] text-gray-500">Vars:</span>
                  {DYNAMIC_VARS.slice(0, 5).map(v => (
                    <button
                      key={v}
                      onClick={() => insertVar(v)}
                      className="text-[10px] bg-blue-50 text-blue-600 px-1 py-0.5 rounded hover:bg-blue-100 font-mono"
                    >
                      {v.replace(/[{}]/g, '')}
                    </button>
                  ))}
                </div>
              </div>

              {/* Editor */}
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full min-h-[180px] p-3 text-sm border border-gray-300 rounded-lg font-mono resize-y focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                style={{ fontSize, textAlign }}
                data-testid={`block-editor-${block.key}`}
              />

              {/* Actions */}
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  {block.is_custom && (
                    <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => onDelete(block.key)}>
                      <Trash2 size={13} className="mr-1" /> Supprimer
                    </Button>
                  )}
                  {!block.is_custom && (
                    <Button variant="ghost" size="sm" onClick={() => onReset(block.key)}>
                      <RotateCcw size={13} className="mr-1" /> Reinitialiser
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={handleCancel} disabled={saving}>
                    <X size={13} className="mr-1" /> Annuler
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={saving} data-testid={`block-save-${block.key}`}>
                    {saving ? '...' : <><Save size={13} className="mr-1" /> Sauvegarder</>}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <>
              <pre
                className="whitespace-pre-wrap text-sm text-gray-700 rounded p-3 max-h-[250px] overflow-y-auto leading-relaxed"
                style={{ fontSize: block.font_size || '12px', textAlign: block.text_align || 'left', backgroundColor: block.bg_color || '#f9fafb' }}
              >
                {block.content}
              </pre>
              <div className="flex items-center justify-between">
                <div className="text-[10px] text-gray-400">
                  {block.updated_at && `Modifie: ${new Date(block.updated_at).toLocaleDateString('fr-FR')}`}
                  {block.font_size && block.font_size !== '12px' && ` | Taille: ${block.font_size}`}
                  {block.bg_color && ` | Fond: ${block.bg_color}`}
                </div>
                <div className="flex gap-2">
                  {!block.is_custom && (
                    <Button variant="ghost" size="sm" onClick={() => onReset(block.key)}>
                      <RotateCcw size={13} className="mr-1" /> Reinitialiser
                    </Button>
                  )}
                  {block.is_custom && (
                    <Button variant="ghost" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => onDelete(block.key)}>
                      <Trash2 size={13} className="mr-1" /> Supprimer
                    </Button>
                  )}
                  <Button size="sm" onClick={() => setEditing(true)} data-testid={`block-edit-${block.key}`}>
                    <Edit3 size={13} className="mr-1" /> Modifier
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function AddBlockModal({ onAdd, onClose, nextOrder }) {
  const [label, setLabel] = useState('');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);

  const handleAdd = async () => {
    if (!label.trim()) { toast.error('Titre requis'); return; }
    setSaving(true);
    try {
      await onAdd({ label, content, order: nextOrder });
      onClose();
    } catch { toast.error('Erreur creation'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl" onClick={e => e.stopPropagation()} data-testid="add-block-modal">
        <div className="p-5 border-b border-gray-100">
          <h3 className="text-lg font-bold text-gray-900">Ajouter un bloc</h3>
          <p className="text-sm text-gray-500 mt-1">Ce bloc sera ajoute a la fin du document</p>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Titre du bloc</label>
            <input
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="Ex: Clause supplementaire"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              autoFocus
              data-testid="add-block-label"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 mb-1 block">Contenu</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Saisissez le contenu du bloc..."
              rows={6}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono resize-y focus:ring-2 focus:ring-blue-500"
              data-testid="add-block-content"
            />
          </div>
        </div>
        <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button onClick={handleAdd} disabled={saving} data-testid="add-block-submit">
            {saving ? '...' : <><Plus size={14} className="mr-1" /> Ajouter</>}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AttestationBlocksAdmin() {
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(new Set());

  const fetchBlocks = useCallback(async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API}/api/admin/attestation-blocks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBlocks(res.data);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBlocks(); }, [fetchBlocks]);

  const headers = { Authorization: `Bearer ${getToken()}` };

  const handleSave = async (key, data) => {
    await axios.put(`${API}/api/admin/attestation-blocks/${key}`, data, { headers });
    toast.success('Bloc sauvegarde');
    await fetchBlocks();
  };

  const handleReset = async (key) => {
    if (!window.confirm('Reinitialiser ce bloc a sa valeur par defaut ?')) return;
    await axios.post(`${API}/api/admin/attestation-blocks/reset/${key}`, {}, { headers });
    toast.success('Bloc reinitialise');
    await fetchBlocks();
  };

  const handleDelete = async (key) => {
    if (!window.confirm('Supprimer ce bloc definitivement ?')) return;
    await axios.delete(`${API}/api/admin/attestation-blocks/${key}`, { headers });
    toast.success('Bloc supprime');
    setSelectedKeys(prev => { const s = new Set(prev); s.delete(key); return s; });
    await fetchBlocks();
  };

  const handleDeleteSelected = async () => {
    if (selectedKeys.size === 0) return;
    if (!window.confirm(`Supprimer ${selectedKeys.size} bloc(s) selectionne(s) ?`)) return;
    await axios.post(`${API}/api/admin/attestation-blocks/delete-many`, { keys: [...selectedKeys] }, { headers });
    toast.success(`${selectedKeys.size} bloc(s) supprime(s)`);
    setSelectedKeys(new Set());
    await fetchBlocks();
  };

  const handleAdd = async (data) => {
    await axios.post(`${API}/api/admin/attestation-blocks`, data, { headers });
    toast.success('Bloc ajoute');
    await fetchBlocks();
  };

  const toggleSelect = (key) => {
    setSelectedKeys(prev => {
      const s = new Set(prev);
      if (s.has(key)) s.delete(key); else s.add(key);
      return s;
    });
  };

  const selectAll = () => {
    if (selectedKeys.size === blocks.length) {
      setSelectedKeys(new Set());
    } else {
      setSelectedKeys(new Set(blocks.map(b => b.key)));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const nextOrder = blocks.length > 0 ? Math.max(...blocks.map(b => b.order || 0)) + 1 : 1;

  return (
    <div className="max-w-5xl mx-auto py-6 px-4" data-testid="attestation-blocks-admin">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Contenu de l'Attestation RCDS</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {blocks.length} blocs | Double-cliquez sur un titre pour le renommer
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} data-testid="add-block-btn">
          <Plus size={15} className="mr-1.5" /> Ajouter un bloc
        </Button>
      </div>

      {/* Bulk actions */}
      {selectedKeys.size > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <span className="text-sm font-medium text-red-700">{selectedKeys.size} bloc(s) selectionne(s)</span>
          <Button variant="destructive" size="sm" onClick={handleDeleteSelected} data-testid="delete-selected-btn">
            <Trash2 size={13} className="mr-1" /> Supprimer la selection
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedKeys(new Set())}>Deselectionner</Button>
        </div>
      )}

      {/* Select all */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <input
          type="checkbox"
          checked={selectedKeys.size === blocks.length && blocks.length > 0}
          onChange={selectAll}
          className="w-4 h-4 rounded border-gray-300"
        />
        <span className="text-xs text-gray-500">Tout selectionner</span>
      </div>

      {/* Blocks list */}
      <div className="space-y-1.5">
        {blocks.map(block => (
          <BlockEditor
            key={block.key}
            block={block}
            onSave={handleSave}
            onReset={handleReset}
            onDelete={handleDelete}
            isSelected={selectedKeys.has(block.key)}
            onSelect={toggleSelect}
          />
        ))}
      </div>

      {showAddModal && (
        <AddBlockModal onAdd={handleAdd} onClose={() => setShowAddModal(false)} nextOrder={nextOrder} />
      )}
    </div>
  );
}
